import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import VFrequencyMark from '../components/VFrequencyMark';

export default function Hero() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    // Trigger entrance animation
    const timer = setTimeout(() => setIsVisible(true), 100);

    // Scroll handler for exit animation
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      
      // Calculate scroll progress (0 to 1) as section leaves viewport
      if (rect.bottom < windowHeight) {
        const progress = Math.min(1, Math.max(0, (windowHeight - rect.bottom) / (windowHeight * 0.5)));
        setScrollProgress(progress);
      } else {
        setScrollProgress(0);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const exitOpacity = Math.max(0, 1 - scrollProgress * 2);
  const exitY = scrollProgress * -100;

  return (
    <section 
      ref={sectionRef}
      className="relative w-full min-h-screen bg-[var(--vyera-cream)] flex items-center justify-center overflow-hidden"
      style={{ zIndex: 10 }}
    >
      {/* V-Frequency Background Mark */}
      <div 
        className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[55vw] max-w-[700px] text-[var(--vyera-text-primary)] pointer-events-none transition-all duration-1000 ${
          isVisible ? 'opacity-10 scale-100' : 'opacity-0 scale-95'
        }`}
        style={{ 
          opacity: 0.1 * exitOpacity,
          transform: `translate(-50%, -50%) scale(${1 + scrollProgress * 0.06})`
        }}
      >
        <VFrequencyMark className="w-full h-auto" />
      </div>

      {/* Content */}
      <div 
        className="relative z-10 text-center px-6"
        style={{ 
          opacity: exitOpacity,
          transform: `translateY(${exitY}px)`
        }}
      >
        <h1 
          className={`vyera-heading-xl text-[var(--vyera-text-primary)] mb-6 transition-all duration-700 delay-300 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <span className="inline-block">Your rhythm never stops.</span>
        </h1>
        
        <p 
          className={`vyera-body text-[var(--vyera-text-secondary)] max-w-md mx-auto mb-10 transition-all duration-700 delay-500 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          Built through repetition.<br />
          Refined over time.
        </p>
        
        <Link 
          to="/shop"
          className={`vyera-cta transition-all duration-700 delay-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          Explore the drop
        </Link>
      </div>

      {/* Scroll indicator */}
      <div 
        className={`absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 transition-all duration-700 delay-1000 ${
          isVisible ? 'opacity-40' : 'opacity-0'
        }`}
      >
        <span className="vyera-label text-[10px] text-[var(--vyera-text-secondary)]">Scroll</span>
        <div className="w-px h-8 bg-[var(--vyera-text-secondary)] animate-pulse" />
      </div>
    </section>
  );
}
